import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

import { ACCENT_BLUE, EASE_OUT, MUTED_GRAY, TEXT_LIGHT } from './constants';

export function ScanlineOverlay({ className = '' }: { className?: string }) {
  return <div className={`pointer-events-none absolute inset-0 ${className}`} style={{ background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(46,205,255,0.02) 2px, rgba(46,205,255,0.02) 4px)' }} aria-hidden='true' />;
}

export function ThemedCard({ children, className = '', delay = 0, glow = ACCENT_BLUE, opaque = false }: { children: React.ReactNode; className?: string; delay?: number; glow?: string; opaque?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }} transition={{ duration: 0.6, delay, ease: EASE_OUT }} className={`relative rounded-xl border ${opaque ? '' : 'backdrop-blur-sm'} ${className}`} style={{ backgroundColor: opaque ? '#0c0e14F0' : `${glow}06`, borderColor: `${glow}30`, boxShadow: `0 0 30px ${glow}10, inset 0 1px 0 ${glow}15` }}>
      <ScanlineOverlay />
      <div className='relative z-10 flex flex-1 flex-col'>{children}</div>
    </motion.div>
  );
}

export function Section({ children, className = '', id, bg }: { children: React.ReactNode; className?: string; id?: string; bg?: React.ReactNode }) {
  return (
    <section id={id} className={`relative px-4 py-14 md:px-8 md:py-20 ${className}`} style={{ zIndex: 1 }}>
      {bg}
      <div className='relative mx-auto max-w-6xl' style={{ zIndex: 1 }}>
        {children}
      </div>
    </section>
  );
}

export function SectionTitle({ subtitle, title, description, accentColor = ACCENT_BLUE }: { subtitle: string; title: string; description?: string; accentColor?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });

  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }} transition={{ duration: 0.5, ease: EASE_OUT }} className='mb-6 text-center md:mb-8'>
      <span className='mb-3 block text-xs font-semibold uppercase tracking-[0.3em] md:text-sm' style={{ color: accentColor, fontFamily: 'var(--font-outfit)' }}>
        {subtitle}
      </span>
      <h2 className='mb-4 text-3xl font-bold md:text-5xl lg:text-6xl' style={{ fontFamily: 'var(--font-bai)', color: TEXT_LIGHT }}>
        {title}
      </h2>
      {description && (
        <p className='mx-auto max-w-2xl text-base md:text-lg' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
          {description}
        </p>
      )}
    </motion.div>
  );
}
